import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import SEO from "../components/SEO";
import { Emblem, Tricornio } from "../components/Emblem";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("La contraseña debe tener al menos 6 caracteres."); return; }
    setLoading(true);
    const res = await register(name, email, password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setErr(res.error);
  };

  return (
    <div className="min-h-screen flex bg-ink relative overflow-hidden">
      <SEO title="Crear cuenta" path="/register" />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />
      <div className="fixed inset-0 bg-dot-grid pointer-events-none opacity-40" />

      <div className="hidden lg:flex w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-ink-700 via-ink-800 to-ink" />
        <div className="absolute top-20 left-1/4 h-[400px] w-[400px] rounded-full bg-gcgold/10 blur-3xl" />

        <div className="relative p-12 flex flex-col justify-between text-white w-full z-10">
          <Link to="/" className="flex items-center gap-2.5 w-fit group">
            <Emblem className="h-10 w-10" />
            <div>
              <div className="font-display font-extrabold tracking-tight">TestOpoGuardiaCivil</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gcgold font-bold font-mono">Élite · Preparación</div>
            </div>
          </Link>

          <div>
            <Tricornio className="w-14 h-9 mb-6 opacity-60" strokeColor="#d4af37" />
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-gcgold mb-4 font-mono">Crear cuenta</div>
            <h2 className="font-display text-5xl xl:text-6xl font-extrabold leading-[0.95] tracking-tighter">
              Empieza<br /><span className="text-gcgold">gratis hoy.</span>
            </h2>
            <ul className="mt-8 space-y-3 text-base text-slate-300">
              {[
                "Acceso a 13.500+ preguntas",
                "Simulacros oficiales cronometrados",
                "Estadísticas y ranking en vivo",
                "Explicaciones detalladas",
              ].map((b) => (
                <li key={b} className="flex items-center gap-3">
                  <CheckCircle2 className="h-4.5 w-4.5 text-gcgold flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="text-xs text-slate-600 font-mono">Sin tarjeta · Cancela cuando quieras</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 relative z-10">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10 w-fit">
            <Emblem className="h-9 w-9" />
            <span className="font-display font-extrabold text-white tracking-tight">TestOpoGuardiaCivil</span>
          </Link>

          <h1 className="font-display text-4xl font-extrabold text-white tracking-tighter">Crear cuenta</h1>
          <p className="mt-2 text-sm text-slate-400">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-gcgold hover:text-gcgold-light font-semibold" data-testid="link-login">
              Inicia sesión →
            </Link>
          </p>

          {err && <Alert variant="destructive" className="mt-6 border-red-900/50 bg-red-950/40 text-red-200" data-testid="register-error"><AlertDescription>{err}</AlertDescription></Alert>}

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 font-mono">Nombre</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre"
                className="mt-2 h-12 bg-ink-800 border-white/10 text-white placeholder:text-slate-600 focus:border-gcgold/40 focus:ring-gcgold/20 rounded-xl"
                data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 font-mono">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com"
                className="mt-2 h-12 bg-ink-800 border-white/10 text-white placeholder:text-slate-600 focus:border-gcgold/40 focus:ring-gcgold/20 rounded-xl"
                data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 font-mono">Contraseña</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres"
                className="mt-2 h-12 bg-ink-800 border-white/10 text-white placeholder:text-slate-600 focus:border-gcgold/40 focus:ring-gcgold/20 rounded-xl"
                data-testid="register-password-input" />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gcgold hover:bg-gcgold-light text-ink font-bold rounded-xl shadow-[0_0_40px_-8px_rgba(212,175,55,0.6)] hover:shadow-[0_0_60px_-8px_rgba(212,175,55,0.9)] transition-all group"
              data-testid="register-submit-button"
            >
              {loading ? "Creando cuenta..." : <>Crear cuenta gratis <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" /></>}
            </Button>

            <p className="text-xs text-slate-600 text-center pt-2">
              Al registrarte aceptas los términos y la política de privacidad.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
